import type { VoucherType, VoucherStatus } from './enums';

/**
 * Voucher system types based on VoucherResponseDto schema from backend
 * @see https://api.payments-dev.splashsports.com/docs
 *
 * `VoucherType` / `VoucherStatus` enums live in `./enums` and are
 * re-exported through the domain barrel; do not re-export them here
 * to avoid duplicate-export collisions.
 */

/**
 * Voucher terms structure matching boost terms format
 */
export interface VoucherTerms {
  type_title: string;
  type_description: string;
  tags: string[];
  eligibility: {
    paragraphs: string[];
    ordered_list?: string[];
  };
  rules: {
    paragraphs: string[];
    ordered_list?: string[];
  };
}

export type VoucherAllowedPayoutType = 'max_payout' | 'protected_payout';

/**
 * Main voucher response interface matching backend VoucherResponseDto
 */
export interface VoucherResponseDto {
  id: string;
  voucherId: string;
  title: string;
  description: string;
  voucherType: VoucherType;
  maxValue: number;
  expiresAt: string;
  status: VoucherStatus;
  restrictedContestId?: string;
  contestName?: string;
  contestType?: string;
  league?: string;
  redeemedContestId?: string;
  transactionId?: string;
  terms?: VoucherTerms;
  terms_html?: string;
  /**
   * Whether protected payouts are allowed when this voucher is applied.
   * Defaults to `false` (protected payouts disabled when voucher is applied).
   * When `true`, allows protected payouts even when voucher is selected.
   * @default false
   */
  isProtectPayoutsAllowed?: boolean;
  allowedPayoutTypes: VoucherAllowedPayoutType[];
}

/**
 * Utility type for voucher eligibility checking
 */
export type VoucherEligibility = {
  isEligible: boolean;
  reason?: string;
};

/**
 * Voucher filter options for UI components
 */
export type VoucherFilters = {
  voucherType?: VoucherType;
  status?: VoucherStatus;
  league?: string;
  searchTerm?: string;
};

/**
 * Voucher sort options for UI components
 */
export type VoucherSortOption = 'expiresAt' | 'maxValue' | 'voucherType' | 'status';

/**
 * Voucher sort direction
 */
export type VoucherSortDirection = 'asc' | 'desc';

/**
 * Voucher sort configuration
 */
export type VoucherSortConfig = {
  sortBy: VoucherSortOption;
  direction: VoucherSortDirection;
};

// Voucher Validation Types and Function

/**
 * Entry context a voucher is being validated for. The backend gates which
 * voucher types are allowed per entry type (EDGE-28444). Only these two
 * literals are ever sent — anything else returns HTTP 400.
 */
// eslint-disable-next-line no-restricted-syntax
export enum VoucherEntryType {
  QUICKPICKS = 'QUICKPICKS',
  CONTEST = 'CONTEST',
}
